import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Chip, CircularProgress,
    TextField, InputAdornment, Select, MenuItem, FormControl,
    Collapse, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton,
} from '@mui/material';
import {
    Search, TableRows, ViewKanban, Group as GroupIcon, ExpandMore, ExpandLess,
    ChatBubbleOutline, Close as CloseIcon, Send,
} from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { Tooltip } from '@mui/material';
import { getRenewalDashboard, getRenewalComments, addRenewalComment } from '../../../Redux/actions';
import { exportRenewalToExcel } from '../../../utils/Common';

// ─── FY helpers ───────────────────────────────────────────────────────────────

function buildFyOptions() {
    const today = new Date();
    const currentFyYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const options = [];
    for (let y = currentFyYear - 2; y <= currentFyYear + 1; y++) {
        options.push({ value: y, label: `FY ${y}-${String(y + 1).slice(2)}` });
    }
    return { options, currentFyYear };
}

// ─── Urgency & status config ───────────────────────────────────────────────────

const URGENCY = {
    red:            { label: '<30 Days',                  color: '#dc2626', bg: '#fee2e2', dot: '#ef4444' },
    amber:          { label: '30-90 Days',                color: '#d97706', bg: '#fef3c7', dot: '#f59e0b' },
    blue:           { label: '>90 Days',                  color: '#2563eb', bg: '#dbeafe', dot: '#3b82f6' },
    expired_recent: { label: 'Recently Expired (0-90d)',  color: '#ea580c', bg: '#fff7ed', dot: '#f97316' },
    expired_old:    { label: 'Expired (>90d ago)',        color: '#374151', bg: '#f3f4f6', dot: '#1f2937' },
};

const STATUS = {
    not_renewed: { label: 'Not Renewed',     color: '#dc2626', bg: '#fee2e2', icon: '✕' },
    pending:     { label: 'Pending (active)', color: '#1d4ed8', bg: '#dbeafe', icon: '⏳' },
};

// ─── Formatters ────────────────────────────────────────────────────────────────

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const fmtAmt = (v) => (v ? INR.format(v) : '₹0');
const fmtAmtShort = (v) => {
    if (!v && v !== 0) return '₹0';
    const abs = Math.abs(v);
    if (abs >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (abs >= 100000)   return `₹${(v / 100000).toFixed(1)}L`;
    if (abs >= 1000)     return `₹${(v / 1000).toFixed(1)}K`;
    return `₹${v}`;
};
const fmtDate = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};
const fmtDays = (days) => (days >= 0 ? `${days}d left` : `${Math.abs(days)}d ago`);

// ─── Shared sub-components ─────────────────────────────────────────────────────

const UrgencyDot = ({ bucket, size = 10 }) => {
    const cfg = URGENCY[bucket];
    return cfg ? (
        <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: cfg.dot, display: 'inline-block', flexShrink: 0 }} />
    ) : null;
};

const StatusChip = ({ status }) => {
    const cfg = STATUS[status];
    if (!cfg) return null;
    return (
        <Chip
            label={`${cfg.icon} ${cfg.label}`}
            size="small"
            sx={{
                fontWeight: 700, fontSize: '0.68rem', height: 24,
                bgcolor: cfg.bg, color: cfg.color,
                border: `1px solid ${cfg.color}33`,
            }}
        />
    );
};

const OwnerLabel = ({ owner }) => (
    <Typography variant="caption" sx={{
        color: owner?.name ? '#1a2535' : '#9ca3af',
        fontStyle: owner?.name ? 'normal' : 'italic',
        fontSize: '0.78rem',
    }}>
        {owner?.name || '— Unassigned'}
    </Typography>
);

// ─── Stats bar ────────────────────────────────────────────────────────────────

const divider = {
    borderRight: '1px solid rgba(255,255,255,0.1)',
    px: 2.5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};
const labelSx = {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.6px',
    color: 'rgba(255,255,255,0.6)',
    textTransform: 'uppercase',
};

const StatsBar = ({ stats }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#1a2535', borderRadius: 1, py: 0.75, px: 0.5, mt: 1.5 }}>
        {[
            { label: 'Open',          value: stats.open,                     color: '#fff'    },
            { label: 'Not Renewed',   value: stats.notRenewed,               color: '#f87171' },
            { label: 'Due ≤30D',      value: stats.dueIn30d,                 color: '#fb923c' },
            { label: 'Unassigned',    value: stats.unassigned,               color: '#facc15' },
            { label: 'Value at Risk', value: fmtAmtShort(stats.valueAtRisk), color: '#4dd0e1' },
        ].map(({ label, value, color }) => (
            <Box key={label} sx={divider}>
                <Typography sx={labelSx}>{label}</Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color, mt: 0.1 }}>{value}</Typography>
            </Box>
        ))}
    </Box>
);

// ─── Table view ────────────────────────────────────────────────────────────────

const COLS = [240, 200, 110, 90, 145, 110, 165, 220, 48];
const COL_HEADERS = ['Client / Facility', 'SO / PO', 'Renewal Due', 'Days', 'Status', 'Value', 'Owner', 'Latest Update', ''];

const TableView = ({ rows, onCommentClick, latestComments }) => {
    if (rows.length === 0) return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>No renewals match the selected filters</Typography>
        </Box>
    );

    return (
        <Box sx={{ overflowX: 'auto', minWidth: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                    <tr style={{ background: '#1a2535' }}>
                        {COL_HEADERS.map((h, i) => (
                            <th key={i} style={{
                                padding: '8px 10px', textAlign: 'left', color: '#fff',
                                fontWeight: 700, fontSize: '10px', letterSpacing: '0.5px',
                                textTransform: 'uppercase', whiteSpace: 'nowrap',
                                minWidth: COLS[i], maxWidth: COLS[i],
                            }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, idx) => {
                        const urg = URGENCY[r.urgency_bucket] || URGENCY.blue;
                        // latestComments overrides after user posts a new comment in the dialog
                        const latest = latestComments[r.salesorder_id] || r.latest_comment;
                        return (
                            <tr key={r.salesorder_id || idx} style={{
                                background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: '1px solid #f0f0f0',
                            }}>
                                {/* Client / Facility */}
                                <td style={{ padding: '10px', borderLeft: `3px solid ${urg.color}`, minWidth: COLS[0], maxWidth: COLS[0] }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#111', lineHeight: 1.25 }}>
                                        {r.facility_name || r.company_name || r.client_group}
                                    </Typography>
                                    {r.facility_name && r.company_name && (
                                        <Typography sx={{ fontSize: '0.72rem', color: '#555', lineHeight: 1.2 }}>{r.company_name}</Typography>
                                    )}
                                    {r.client_group && (
                                        <Typography sx={{ fontSize: '0.7rem', color: '#999', lineHeight: 1.2 }}>{r.client_group}</Typography>
                                    )}
                                </td>

                                {/* SO / PO */}
                                <td style={{ padding: '10px', minWidth: COLS[1], maxWidth: COLS[1] }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#008080', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {r.so_number}
                                    </Typography>
                                    {(r.contract_type || r.billing_cycle) && (
                                        <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', mt: 0.5,
                                            px: 0.75, py: 0.15, borderRadius: '4px',
                                            bgcolor: '#f0f0f0',
                                        }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: '#555', fontWeight: 500, lineHeight: 1.4 }}>
                                                {[r.contract_type, r.billing_cycle].filter(Boolean).join(' · ')}
                                            </Typography>
                                        </Box>
                                    )}
                                </td>

                                {/* Renewal Due */}
                                <td style={{ padding: '10px', minWidth: COLS[2] }}>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>
                                        {fmtDate(r.po_expiry_date)}
                                    </Typography>
                                </td>

                                {/* Days */}
                                <td style={{ padding: '10px', minWidth: COLS[3] }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: urg.color }}>
                                        {fmtDays(r.days_until_expiry)}
                                    </Typography>
                                </td>

                                {/* Status */}
                                <td style={{ padding: '10px', minWidth: COLS[4] }}>
                                    <StatusChip status={r.tracker_status} />
                                </td>

                                {/* Value */}
                                <td style={{ padding: '10px', minWidth: COLS[5] }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#222' }}>
                                        {fmtAmt(r.contract_value)}
                                    </Typography>
                                </td>

                                {/* Owner */}
                                <td style={{ padding: '10px', minWidth: COLS[6] }}>
                                    <OwnerLabel owner={r.renewal_owner} />
                                </td>

                                {/* Latest Update — clickable */}
                                <td
                                    style={{ padding: '10px', minWidth: COLS[7], maxWidth: COLS[7], cursor: 'pointer' }}
                                    onClick={() => onCommentClick(r)}
                                >
                                    {latest ? (() => {
                                        const { author, body } = parseComment(latest);
                                        return (
                                            <>
                                                <Typography sx={{ fontSize: '0.72rem', color: '#374151', lineHeight: 1.4,
                                                    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                    {body}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af', mt: 0.3 }}>
                                                    {author && `${author} · `}{latest.date}
                                                </Typography>
                                            </>
                                        );
                                    })() : (
                                        <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontStyle: 'italic' }}>
                                            No updates — add one
                                        </Typography>
                                    )}
                                </td>

                                {/* Comment icon */}
                                <td style={{ padding: '6px', minWidth: COLS[8], textAlign: 'center' }}>
                                    <IconButton size="small" onClick={() => onCommentClick(r)} sx={{ color: '#9ca3af', '&:hover': { color: '#008080' } }}>
                                        <ChatBubbleOutline sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </Box>
    );
};

// ─── Board view ────────────────────────────────────────────────────────────────

const BoardCard = ({ r }) => {
    const urg = URGENCY[r.urgency_bucket] || URGENCY.blue;
    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, borderLeft: `3px solid ${urg.color}` }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#111', lineHeight: 1.25 }}>
                {r.facility_name || r.company_name || r.client_group}
            </Typography>
            {r.client_group && r.facility_name && (
                <Typography sx={{ fontSize: '0.7rem', color: '#999', mt: 0.2 }}>{r.client_group}</Typography>
            )}
            <Typography sx={{ fontSize: '0.72rem', color: '#008080', fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-all' }}>
                {r.so_number}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#222' }}>{fmtAmtShort(r.contract_value)}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: urg.color, fontWeight: 700 }}>{fmtDays(r.days_until_expiry)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
                <StatusChip status={r.tracker_status} />
                <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', fontStyle: 'italic' }}>
                    {r.renewal_owner?.name || 'Unassigned'}
                </Typography>
            </Box>
        </Paper>
    );
};

const BoardView = ({ rows }) => (
    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, pt: 1 }}>
        {Object.entries(URGENCY).map(([key, cfg]) => {
            const laneRows = rows.filter(r => r.urgency_bucket === key);
            return (
                <Box key={key} sx={{ minWidth: 260, flex: '0 0 260px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.5, py: 1, bgcolor: cfg.bg, borderRadius: 2 }}>
                        <UrgencyDot bucket={key} size={12} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: cfg.color, flex: 1 }}>{cfg.label}</Typography>
                        <Chip label={laneRows.length} size="small" sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: cfg.color, color: '#fff', height: 20 }} />
                    </Box>
                    {laneRows.length === 0 ? (
                        <Typography sx={{ color: '#bbb', fontSize: '0.75rem', textAlign: 'center', py: 3 }}>None</Typography>
                    ) : (
                        laneRows.map((r, i) => <BoardCard key={r.salesorder_id || i} r={r} />)
                    )}
                </Box>
            );
        })}
    </Box>
);

// ─── By Owner view ─────────────────────────────────────────────────────────────

const ByOwnerView = ({ rows }) => {
    const [collapsed, setCollapsed] = useState({});

    const groups = useMemo(() => {
        const map = {};
        for (const r of rows) {
            const key = r.renewal_owner?.email || '__unassigned__';
            const label = r.renewal_owner?.name || '— Unassigned';
            if (!map[key]) map[key] = { label, rows: [], value: 0 };
            map[key].rows.push(r);
            map[key].value += r.contract_value || 0;
        }
        return Object.entries(map).sort(([aKey, { label: aLabel }], [bKey, { label: bLabel }]) => {
            if (aKey === '__unassigned__') return 1;
            if (bKey === '__unassigned__') return -1;
            return aLabel.localeCompare(bLabel);
        });
    }, [rows]);

    const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

    if (groups.length === 0) return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>No renewals match the selected filters</Typography>
        </Box>
    );

    return (
        <Box sx={{ mt: 1 }}>
            {groups.map(([key, { label, rows: gRows, value }]) => (
                <Paper key={key} variant="outlined" sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                        onClick={() => toggle(key)}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
                            bgcolor: '#f8fafc', cursor: 'pointer',
                            '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a2535', flex: 1 }}>{label}</Typography>
                        <Chip label={`${gRows.length} renewal${gRows.length !== 1 ? 's' : ''}`} size="small"
                            sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#e2e8f0', color: '#475569' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#475569' }}>{fmtAmtShort(value)}</Typography>
                        {collapsed[key] ? <ExpandMore sx={{ color: '#94a3b8' }} /> : <ExpandLess sx={{ color: '#94a3b8' }} />}
                    </Box>
                    <Collapse in={!collapsed[key]}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <tbody>
                                    {gRows.map((r, idx) => {
                                        const urg = URGENCY[r.urgency_bucket] || URGENCY.blue;
                                        return (
                                            <tr key={r.salesorder_id || idx} style={{ borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
                                                <td style={{ padding: '8px 10px', borderLeft: `3px solid ${urg.color}`, width: 220 }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#111', lineHeight: 1.2 }}>
                                                        {r.facility_name || r.company_name || r.client_group}
                                                    </Typography>
                                                    {r.client_group && r.facility_name && (
                                                        <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>{r.client_group}</Typography>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px 10px', width: 180 }}>
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#008080', fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}>
                                                        {r.so_number}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>
                                                        {[r.contract_type, r.billing_cycle].filter(Boolean).join(' · ')}
                                                    </Typography>
                                                </td>
                                                <td style={{ padding: '8px 10px', width: 110 }}>
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#333' }}>{fmtDate(r.po_expiry_date)}</Typography>
                                                </td>
                                                <td style={{ padding: '8px 10px', width: 90 }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: urg.color }}>
                                                        {fmtDays(r.days_until_expiry)}
                                                    </Typography>
                                                </td>
                                                <td style={{ padding: '8px 10px', width: 145 }}>
                                                    <StatusChip status={r.tracker_status} />
                                                </td>
                                                <td style={{ padding: '8px 10px', width: 110, textAlign: 'right' }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#222' }}>
                                                        {fmtAmt(r.contract_value)}
                                                    </Typography>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>
                    </Collapse>
                </Paper>
            ))}
        </Box>
    );
};

// ─── Comment Dialog ────────────────────────────────────────────────────────────

const fmtCommentDate = (dateStr, timeStr) => {
    if (!dateStr) return '';
    return `${dateStr}${timeStr ? '  ' + timeStr : ''}`;
};

// Zoho always attributes comments to the OAuth service account.
// We embed the app user's name as "[Name]: " prefix in the description.
// This parser extracts it back so the UI shows the correct author.
const parseComment = (c) => {
    const match = c.description?.match(/^\[(.+?)\]:\s*/);
    if (match) {
        return { author: match[1], body: c.description.slice(match[0].length) };
    }
    return { author: c.commented_by || '', body: c.description || '' };
};

const CommentDialog = ({ open, row, onClose, dispatch }) => {
    const currentUser = useSelector(st => st.currentUser);
    const [comments, setComments]     = useState([]);
    const [loading, setLoading]       = useState(false);
    const [posting, setPosting]       = useState(false);
    const [text, setText]             = useState('');
    const [error, setError]           = useState('');

    useEffect(() => {
        if (!open || !row?.salesorder_id) return;
        setLoading(true);
        setComments([]);
        setError('');
        dispatch(getRenewalComments(row.salesorder_id)).then((resp) => {
            if (resp?.data?.success) setComments(resp.data.comments || []);
            else setError('Failed to load comments.');
            setLoading(false);
        }).catch(() => { setError('Failed to load comments.'); setLoading(false); });
    }, [open, row?.salesorder_id]);

    const handlePost = () => {
        if (!text.trim()) return;
        setPosting(true);
        const userName = currentUser?.name || '';
        const description = userName ? `[${userName}]: ${text.trim()}` : text.trim();
        dispatch(addRenewalComment(row.salesorder_id, { description })).then((resp) => {
            if (resp?.data?.success) {
                setText('');
                // Reload comments
                dispatch(getRenewalComments(row.salesorder_id)).then((r) => {
                    if (r?.data?.success) setComments(r.data.comments || []);
                });
            } else {
                setError('Failed to post comment.');
            }
            setPosting(false);
        }).catch(() => { setError('Failed to post comment.'); setPosting(false); });
    };

    return (
        <Dialog open={open} onClose={() => onClose(comments)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a2535' }}>
                        {row?.facility_name || row?.company_name || row?.client_group}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#008080', fontFamily: 'monospace', mt: 0.3 }}>
                        {row?.so_number}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => onClose(comments)} sx={{ mt: -0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2, minHeight: 200 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} sx={{ color: '#008080' }} />
                    </Box>
                ) : error ? (
                    <Typography sx={{ color: '#dc2626', fontSize: '0.82rem', textAlign: 'center', py: 3 }}>{error}</Typography>
                ) : comments.length === 0 ? (
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.82rem', textAlign: 'center', py: 3, fontStyle: 'italic' }}>
                        No comments yet — be the first to add one.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {comments.map((c, i) => {
                            const { author, body } = parseComment(c);
                            return (
                                <Box key={c.comment_id || i} sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, borderLeft: '3px solid #008080' }}>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#111', lineHeight: 1.5 }}>{body}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', mt: 0.5 }}>
                                        {author && <strong style={{ color: '#555' }}>{author}</strong>}
                                        {author && '  ·  '}
                                        {fmtCommentDate(c.date, c.time)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                <TextField
                    multiline
                    rows={2}
                    fullWidth
                    placeholder="Add a comment..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#008080' } } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => onClose(comments)} variant="outlined" size="small"
                        sx={{ borderColor: '#d1d5db', color: '#555', '&:hover': { borderColor: '#9ca3af' } }}>
                        Close
                    </Button>
                    <Button
                        onClick={handlePost}
                        disabled={!text.trim() || posting}
                        variant="contained"
                        size="small"
                        startIcon={posting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Send sx={{ fontSize: 14 }} />}
                        sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' }, '&:disabled': { bgcolor: '#ccc' } }}
                    >
                        {posting ? 'Posting…' : 'Post'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

// ─── View toggle button ────────────────────────────────────────────────────────

const ViewBtn = ({ active, onClick, icon, label }) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.7,
            cursor: 'pointer', borderRadius: 1, fontSize: '12px', fontWeight: 600,
            border: '1px solid',
            borderColor: active ? '#008080' : '#d1d5db',
            bgcolor: active ? '#008080' : '#fff',
            color: active ? '#fff' : '#555',
            userSelect: 'none',
            '&:hover': { borderColor: '#008080', color: active ? '#fff' : '#008080' },
        }}
    >
        {icon} {label}
    </Box>
);

// ─── Main component ────────────────────────────────────────────────────────────

export default function Renewal({ queryParams }) {
    const dispatch = useDispatch();
    const { options: fyOptions, currentFyYear } = buildFyOptions();
    const [loading, setLoading]               = useState(true);
    const [dashData, setDashData]             = useState(null);
    const [fyYear, setFyYear]                 = useState(currentFyYear);
    const [subTab, setSubTab]                 = useState(0);
    const [search, setSearch]                 = useState('');
    const [urgencyFilter, setUrgencyFilter]   = useState('all');
    const [statusFilter, setStatusFilter]     = useState('all');
    const [ownerFilter, setOwnerFilter]       = useState('all');
    const [stateFilter, setStateFilter]       = useState('all');
    const [viewMode, setViewMode]             = useState('table');
    const [downloading, setDownloading]       = useState(false);
    // Comment dialog state
    const [commentRow, setCommentRow]         = useState(null);
    // Cache latest comment per salesorder_id after dialog loads
    const [latestComments, setLatestComments] = useState({});
    // Tracks every salesorder_id that has been fetched this session (including those with no comments),
    // so we never re-fetch the same SO when switching FY back and forth.
    const fetchedCommentIds = useRef(new Set());

    useEffect(() => { fetchData(); }, [queryParams, fyYear]);

    const fetchData = (clearComments = false) => {
        setLoading(true);
        if (clearComments) {
            setLatestComments({});
            fetchedCommentIds.current = new Set();
        }
        const existingParams = Object.fromEntries([...queryParams]);
        dispatch(getRenewalDashboard({
            type: 'all',
            clientName: existingParams?.client_group || '',
            fy: fyYear,
        })).then((resp) => {
            if (resp?.data?.success) setDashData(resp.data.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    // Bulk per-SO comment prefetch (one Zoho call per row on tab load) is disabled to avoid
    // hammering the Zoho rate limit — comments now only load on demand via the CommentDialog
    // when a user clicks into a specific row.
    // useEffect(() => {
    //     if (!dashData?.renewalRows) return;
    //     const allRows = [
    //         ...(dashData.renewalRows.subscription_ocms || []),
    //         ...(dashData.renewalRows.sales_software || []),
    //     ].filter(r => r.salesorder_id && !r.latest_comment && !fetchedCommentIds.current.has(r.salesorder_id));

    //     if (allRows.length === 0) return;

    //     let cancelled = false;
    //     (async () => {
    //         for (const r of allRows) {
    //             if (cancelled) break;
    //             // Mark as fetched immediately so a concurrent FY switch doesn't duplicate the call
    //             fetchedCommentIds.current.add(r.salesorder_id);
    //             try {
    //                 const resp = await dispatch(getRenewalComments(r.salesorder_id));
    //                 if (resp?.data?.success && resp.data.comments?.length > 0) {
    //                     // Zoho returns comments newest-first
    //                     setLatestComments(prev => ({ ...prev, [r.salesorder_id]: resp.data.comments[0] }));
    //                 }
    //             } catch { /* ignore per-SO failures */ }
    //             await new Promise(resolve => setTimeout(resolve, 150));
    //         }
    //     })();
    //     return () => { cancelled = true; };
    // }, [dashData]);

    const activeKey = subTab === 0 ? 'subscription_ocms' : 'sales_software';
    const tabRows   = dashData?.renewalRows?.[activeKey] || [];

    // Unique owners for the current sub-tab
    const ownerList = useMemo(() => {
        const map = new Map();
        for (const r of tabRows) {
            if (r.renewal_owner?.email) map.set(r.renewal_owner.email, r.renewal_owner);
        }
        return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [tabRows]);

    // Stats from full (unfiltered) sub-tab rows
    const stats = useMemo(() => ({
        open:         tabRows.length,
        notRenewed:   tabRows.filter(r => r.tracker_status === 'not_renewed').length,
        dueIn30d:     tabRows.filter(r => r.urgency_bucket === 'red').length,
        unassigned:   tabRows.filter(r => !r.renewal_owner).length,
        valueAtRisk:  tabRows
            .filter(r => r.tracker_status === 'not_renewed' || r.urgency_bucket === 'red')
            .reduce((s, r) => s + (r.contract_value || 0), 0),
    }), [tabRows]);

    // Client-side filtered rows
    const filteredRows = useMemo(() => {
        let rows = tabRows;
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(r =>
                r.facility_name?.toLowerCase().includes(q) ||
                r.company_name?.toLowerCase().includes(q) ||
                r.client_group?.toLowerCase().includes(q) ||
                r.so_number?.toLowerCase().includes(q)
            );
        }
        if (stateFilter === 'active')  rows = rows.filter(r => r.facility_status !== 'INACTIVE' && r.facility_status !== 'ON_HOLD');
        else if (stateFilter === 'on_hold') rows = rows.filter(r => r.facility_status === 'ON_HOLD');
        else if (stateFilter === 'closed')  rows = rows.filter(r => r.facility_status === 'INACTIVE');
        if (urgencyFilter !== 'all') rows = rows.filter(r => r.urgency_bucket === urgencyFilter);
        if (statusFilter !== 'all')  rows = rows.filter(r => r.tracker_status === statusFilter);
        if (ownerFilter === '__unassigned__') rows = rows.filter(r => !r.renewal_owner);
        else if (ownerFilter !== 'all')       rows = rows.filter(r => r.renewal_owner?.email === ownerFilter);
        return rows;
    }, [tabRows, search, stateFilter, urgencyFilter, statusFilter, ownerFilter]);

    const ocmsCount  = dashData?.renewalRows?.subscription_ocms?.length ?? 0;
    const salesCount = dashData?.renewalRows?.sales_software?.length ?? 0;

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: '#008080' }} />
        </Box>
    );

    if (!dashData) return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">Failed to load renewal data.</Typography>
        </Box>
    );

    const handleDownload = () => {
        if (!filteredRows.length) return;
        setDownloading(true);
        try {
            const label = subTab === 0 ? 'Subscription & OCMS' : 'Sales & Software Subscription';
            exportRenewalToExcel(filteredRows, latestComments, label, fyYear);
        } finally {
            setDownloading(false);
        }
    };

    const handleCommentClick = (row) => setCommentRow(row);
    const handleCommentClose = (updatedComments) => {
        // Zoho returns comments newest-first, so index 0 is the latest
        if (updatedComments?.length && commentRow?.salesorder_id) {
            setLatestComments(prev => ({
                ...prev,
                [commentRow.salesorder_id]: updatedComments[0],
            }));
        }
        setCommentRow(null);
    };

    const tabBase   = { px: 2.5, py: 1.2, cursor: 'pointer', fontSize: '13px', fontWeight: 600, borderBottom: '2px solid transparent', color: '#777', mr: 0.5, userSelect: 'none' };
    const tabActive = { ...tabBase, color: '#008080', borderBottomColor: '#008080' };

    const selectSx = { fontSize: '0.82rem', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } };

    return (
        <Box sx={{ pb: 4 }}>
            {/* Sub-tabs row — tabs on left, view toggles + download on right */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={subTab === 0 ? tabActive : tabBase} onClick={() => setSubTab(0)}>
                        Subscription & OCMS ({ocmsCount})
                    </Box>
                    <Box sx={subTab === 1 ? tabActive : tabBase} onClick={() => setSubTab(1)}>
                        Sales & Software Subscription ({salesCount})
                    </Box>
                </Box>

                {/* View toggle + Download */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pb: 0.5 }}>
                    <ViewBtn active={viewMode === 'table'}    onClick={() => setViewMode('table')}    icon={<TableRows sx={{ fontSize: 16 }} />}  label="Table"    />
                    <ViewBtn active={viewMode === 'board'}    onClick={() => setViewMode('board')}    icon={<ViewKanban sx={{ fontSize: 16 }} />}  label="Board"    />
                    <ViewBtn active={viewMode === 'by_owner'} onClick={() => setViewMode('by_owner')} icon={<GroupIcon sx={{ fontSize: 16 }} />}   label="By Owner" />
                    <Tooltip title="Download as Excel">
                        <span>
                            <IconButton
                                onClick={handleDownload}
                                disabled={downloading || !filteredRows.length}
                                size="small"
                                sx={{ ml: 0.5, color: '#555', '&:hover': { color: '#008080' } }}
                            >
                                {downloading
                                    ? <CircularProgress size={18} sx={{ color: '#008080' }} />
                                    : <DownloadIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filter row — right-aligned */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                {/* Financial Year */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                        value={fyYear}
                        onChange={(e) => setFyYear(e.target.value)}
                        sx={{ ...selectSx, fontWeight: 700, color: '#008080' }}
                        MenuProps={{ disableScrollLock: true }}
                    >
                        {fyOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.82rem' }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* State */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
                        sx={selectSx} MenuProps={{ disableScrollLock: true }}>
                        <MenuItem value="all">All States</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="on_hold">On Hold</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                </FormControl>

                {/* Search */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    width: 220, px: 1.25, py: '6px',
                    border: '1px solid rgba(0,0,0,0.23)', borderRadius: '4px', bgcolor: '#fff',
                    '&:focus-within': { border: '1px solid #008080' },
                }}>
                    <Search sx={{ fontSize: 18, color: '#9ca3af', flexShrink: 0 }} />
                    <input
                        placeholder="Client / SO / Facility..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, minWidth: 0, background: 'transparent' }}
                    />
                    {search && (
                        <IconButton size="small" onClick={() => setSearch('')} sx={{ p: '2px', flexShrink: 0, color: '#9ca3af', '&:hover': { color: '#555' } }}>
                            <CloseIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    )}
                </Box>

                {/* Urgency */}
                <FormControl size="small" sx={{ minWidth: 175 }}>
                    <Select value={urgencyFilter} onChange={(e) => setUrgencyFilter(e.target.value)}
                        sx={selectSx} MenuProps={{ disableScrollLock: true }}>
                        <MenuItem value="all">All Urgency</MenuItem>
                        {Object.entries(URGENCY).map(([key, cfg]) => (
                            <MenuItem key={key} value={key}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.dot }} />
                                    {cfg.label}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Renewal Status */}
                <FormControl size="small" sx={{ minWidth: 175 }}>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        sx={selectSx} MenuProps={{ disableScrollLock: true }}>
                        <MenuItem value="all">All Renewal Status</MenuItem>
                        <MenuItem value="not_renewed">✕ Not Renewed</MenuItem>
                        <MenuItem value="pending">⏳ Pending (active)</MenuItem>
                    </Select>
                </FormControl>

                {/* Owner */}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
                        sx={selectSx} MenuProps={{ disableScrollLock: true }}>
                        <MenuItem value="all">All Owners</MenuItem>
                        <MenuItem value="__unassigned__">— Unassigned</MenuItem>
                        {ownerList.map(o => (
                            <MenuItem key={o.email} value={o.email}>{o.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

            </Box>

            {/* Stats bar */}
            <StatsBar stats={stats} />

            {/* Content */}
            <Paper variant="outlined" sx={{ mt: 1.5, borderRadius: 2, overflow: 'hidden' }}>
                {viewMode === 'table' && (
                    <TableView
                        rows={filteredRows}
                        onCommentClick={handleCommentClick}
                        latestComments={latestComments}
                    />
                )}
                {viewMode === 'board' && (
                    <Box sx={{ p: 2 }}>
                        <BoardView rows={filteredRows} />
                    </Box>
                )}
                {viewMode === 'by_owner' && (
                    <Box sx={{ p: 2 }}>
                        <ByOwnerView rows={filteredRows} />
                    </Box>
                )}
            </Paper>

            <CommentDialog
                open={!!commentRow}
                row={commentRow}
                onClose={handleCommentClose}
                dispatch={dispatch}
            />
        </Box>
    );
}
